import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppContext } from "../../../classes/contexts/AppContext";
import { useRemoteRenderContext } from "../../../classes/contexts/RemoteRenderContext";
import { VertebraEnumAnatomicalOrder } from "../../../classes/enums/AnnotationsEnum";
import IconsEnum from "../../../classes/enums/IconsEnum";
import { SPINESIM_MODALITIES } from "../../../classes/enums/MedicalImagesEnum";
import { PLANNING_STATES } from "../../../classes/enums/PlanningStateEnum";
import {
  MacroRegions,
  SpineRegions,
} from "../../../classes/enums/ValidationEnums";
import { vertebraUtils } from "../../../classes/utils/AnnotationsUtils";
import Button from "../../../components/button/Button";
import ButtonWithIcon from "../../../components/button/ButtonWithIcon";
import RadioButtonFlat from "../../../components/radioButtons/RadioButtonFlat";
import LabeledSelect from "../../../components/select/LabeledSelect";
import Select from "../../../components/select/Select";
import SimpleWidgetBox from "../../../components/simpleWidgetBox/SimpleWidgetBox";
import WidgetBox from "../../../components/widgetBox/WidgetBox";
import { useRouteState } from "../../../hooks/useRouteState";
import { useLandmarksSanityCheck } from "../../../hooks/useLandmarksSanityCheck";
import {
  createAnnotationsInDatabase,
  deleteAnnotationsInDatabase,
  updateAnnotationsInDatabase,
} from "../../../services/AnnotationsService";
import {
  updateInstanceMetadata,
  validateInstanceInDatabase,
} from "../../../services/InstancesService";
import ValidationConfirmationModal from "../validationConfirmationModal/ValidationConfirmationModal";
import "./ValidationTimeline.css";

const stepsDx = [
  {
    name: "Spine",
    isSpineGroup: true,
    subtasksLabel: "Review:",
    note: "Adjust by dragging and dropping the points, or by adding/removing vertebrae.",
    spineOptions: [
      {
        name: "All",
        region: SpineRegions.FULL_SPINE,
        subtasks: [
          "Vertebral labels (count and names)",
          "Endplate position",
          "Pedicle position",
        ],
      },
      {
        name: "Cerv",
        region: SpineRegions.CERVICAL,
        subtasks: [
          "Vertebral labels (count and names)",
          "Endplate position",
          "Pedicle position",
        ],
      },
      {
        name: "Thor",
        region: SpineRegions.THORACIC,
        subtasks: [
          "Vertebral labels (count and names)",
          "Endplate position",
          "Pedicle position",
        ],
      },
      {
        name: "Lumb",
        region: SpineRegions.LUMBAR,
        subtasks: [
          "Vertebral labels (count and names)",
          "Endplate position",
          "Pedicle position",
        ],
      },
    ],
  },
  {
    name: "Functional Endplate",
    contentType: "functionalEndplate",
    subtasks: [
      "Confirm the functional vertebra endplate for spinopelvic parameter calculation (e.g., pelvic incidence, sacral slope).",
    ],
    region: SpineRegions.LUMBAR,
  },
  {
    name: "Pelvis and knee",
    subtasksLabel: "Review annotations of the following anatomical landmarks:",
    subtasks: [
      "Femoral heads (sagittal view only).",
      "Knees (sagittal view only).",
      "Ankles (sagittal view only).",
      "Iliac crests (highest points, coronal view only).",
    ],
    note: "Adjust by dragging and dropping points, or by adding/removing annotations. Ensure femoral head labels do not overlap when displayed.",
    region: SpineRegions.LOWER_BODY,
  },
];

const stepsDrr = [
  {
    name: "Identify Vertebral Body",
    subtasks: [
      "Click on the center of each vertebral body to place a point and label the vertebra.",
    ],
    region: null,
  },
];

const ValidationTimeline = ({
  instances,
  isLoadingViews,
  refreshData,
  validationMode,
  onRegionChange,
  allDrrLabelsAssigned = true,
  onRerunLandmarking,
  viewReloadToken = 0,
  disabled = false,
  onCheckReloadRequested,
  annotationsMap = null,
}) => {
  const isDxMode = validationMode === SPINESIM_MODALITIES.DX;

  const [currentStep, setCurrentStep] = useState(0);
  const [validatedSteps, setValidatedSteps] = useState(new Set());
  const [isUpdating, setIsUpdating] = useState(false);
  const [, setError] = useState(null);

  const [activeSpineFilter, setActiveSpineFilter] = useState("All");

  const { client: renderClient } = useRemoteRenderContext();
  const { showPopup, hidePopup } = useAppContext();
  const routeState = useRouteState();
  const { navigateToCheck } = routeState;
  const { isChecking, checkMultipleAnnotations } = useLandmarksSanityCheck();

  const vertebraeItems = useMemo(() => {
    return Object.keys(VertebraEnumAnatomicalOrder)
      .map(key => {
        const vertebraName = VertebraEnumAnatomicalOrder[key];
        const centroidId = vertebraUtils.getCentroidEntity(vertebraName);

        return {
          label: vertebraName,
          value: centroidId,
        };
      })
      .filter(option => option.value !== null);
  }, []);

  const [firstVisibleVertebra, setFirstVisibleVertebra] = useState(
    vertebraeItems[0]?.value || ""
  );

  const transitionalVertebraOptions = [
    { label: "Yes", value: "yes" },
    { label: "No", value: "no" },
  ];
  const t13Options = [
    { label: "Yes", value: "yes" },
    { label: "No", value: "no" },
  ];
  const l6Options = [
    { label: "S1 Lumbarized", value: "S1L" },
    { label: "L5 Sacralized", value: "L5S" },
    { label: "None", value: "None" },
  ];

  const [hasTransitionalVertebra, setHasTransitionalVertebra] = useState(false);
  const [hasT13, setHasT13] = useState(false);
  const [l5S1Variation, setL5S1Variation] = useState("None");
  const [lastRerunTransitionalState, setLastRerunTransitionalState] =
    useState(null);
  const lastRerunInitializationTokenRef = useRef(null);
  const lastBaselineInstanceIdRef = useRef(null);

  const currentTransitionalState = useMemo(
    () => ({
      hasTransitionalVertebra,
      hasT13,
      l5S1Variation,
    }),
    [hasTransitionalVertebra, hasT13, l5S1Variation]
  );

  const hasPendingTransitionalChanges = useMemo(() => {
    if (!lastRerunTransitionalState) return false;

    return (
      lastRerunTransitionalState.hasTransitionalVertebra !==
        currentTransitionalState.hasTransitionalVertebra ||
      lastRerunTransitionalState.hasT13 !== currentTransitionalState.hasT13 ||
      lastRerunTransitionalState.l5S1Variation !==
        currentTransitionalState.l5S1Variation
    );
  }, [currentTransitionalState, lastRerunTransitionalState]);

  const shouldBlockTransitionalNavigation =
    isDxMode && hasPendingTransitionalChanges;

  const [functionalEndplateOptions, setFunctionalEndplateOptions] = useState(
    []
  );
  const [functionalEndplate, setFunctionalEndplate] = useState("");
  const [functionalEndplateTouched, setFunctionalEndplateTouched] =
    useState(false);
  const [isRerunExpanded, setIsRerunExpanded] = useState(false);

  const functionalEndplateLabels = useMemo(() => {
    return functionalEndplateOptions.map(option => option.label);
  }, [functionalEndplateOptions]);

  useEffect(() => {
  }, [functionalEndplate]);

  const activeDxSteps = useMemo(() => {
    if (!hasTransitionalVertebra) {
      return stepsDx.filter(step => step.contentType !== "functionalEndplate");
    }
    return stepsDx;
  }, [hasTransitionalVertebra]);

  const drrAnnotationContext = useMemo(() => {
    if (
      !annotationsMap ||
      !instances ||
      !instances.ct_instance_id ||
      !instances.ct_instance_id._id
    ) {
      return null;
    }

    const ctInstanceId = instances.ct_instance_id._id;
    const ctAnnotations = annotationsMap[ctInstanceId] || [];
    const annotationIdByUniqueId = new Map();

    ctAnnotations.forEach(annotation => {
      if (
        !annotation ||
        !annotation._id ||
        annotation.unique_identifier === undefined ||
        annotation.unique_identifier === null
      ) {
        return;
      }

      const uniqueId = Number(annotation.unique_identifier);
      if (Number.isNaN(uniqueId)) {
        return;
      }

      const annotationName = annotation.name || "";
      if (
        typeof annotationName === "string" &&
        !annotationName.startsWith("drr_validation_")
      ) {
        return;
      }

      annotationIdByUniqueId.set(uniqueId, annotation._id);
    });

    return {
      instanceId: ctInstanceId,
      annotationIdByUniqueId,
    };
  }, [annotationsMap, instances]);

  /**
   * Build anno_id_dict from annotationsMap for sanity checking.
   * Merges database annotations with pending updates from the render client.
   * Returns annotations for both sagittal and coronal when in DX mode.
   */
  const buildAnnoIdDict = useCallback(
    async () => {
      if (!annotationsMap || !instances) {
        return null;
      }

      const buildDictForInstance = async instanceId => {
        if (!instanceId) return null;

        const dbAnnotations = annotationsMap[instanceId] || [];
        const annoIdDict = {};

        // Build dict from DB annotations
        // Include all annotations, even those with null values (unplaced points)
        // The sanity check library needs ALL landmark IDs to detect the view
        dbAnnotations.forEach(annotation => {
          if (annotation && annotation.unique_identifier !== undefined) {
            const uniqueId = String(annotation.unique_identifier);
            const value = annotation.representation?.[0]?.value;
            // Include null values - sanity check needs the key to exist
            annoIdDict[uniqueId] = value || null;
          }
        });

        return annoIdDict;
      };

      if (validationMode === SPINESIM_MODALITIES.DX) {
        const sagittalId = instances.sagittal_dx_instance_id?._id;
        const coronalId = instances.coronal_dx_instance_id?._id;

        const [sagittalDict, coronalDict] = await Promise.all([
          buildDictForInstance(sagittalId),
          buildDictForInstance(coronalId),
        ]);

        if (renderClient) {
          try {
            const updates = await renderClient.getUpdatedAnnotations(
              PLANNING_STATES.VALIDATE
            );

            if (sagittalDict && updates.lateral) {
              const viewData = updates.lateral;
              if (viewData?.updates) {
                Object.values(viewData.updates).forEach(update => {
                  if (update.unique_identifier !== undefined) {
                    sagittalDict[String(update.unique_identifier)] =
                      update.values ?? null;
                  }
                });
              }
              if (viewData?.additions) {
                Object.entries(viewData.additions).forEach(
                  ([uniqueId, data]) => {
                    if (data.values) {
                      sagittalDict[uniqueId] = data.values;
                    }
                  }
                );
              }
            }

            if (coronalDict && updates.frontal) {
              const viewData = updates.frontal;
              if (viewData?.updates) {
                Object.values(viewData.updates).forEach(update => {
                  if (update.unique_identifier !== undefined) {
                    coronalDict[String(update.unique_identifier)] =
                      update.values ?? null;
                  }
                });
              }
              if (viewData?.additions) {
                Object.entries(viewData.additions).forEach(
                  ([uniqueId, data]) => {
                    if (data.values) {
                      coronalDict[uniqueId] = data.values;
                    }
                  }
                );
              }
            }
          } catch (error) {
            console.error(
              "[ValidationTimeline] Error getting updated annotations:",
              error
            );
          }
        }

        return { sagittal: sagittalDict, coronal: coronalDict };
      }

      if (validationMode === SPINESIM_MODALITIES.DRR) {
        const instanceId = instances.ct_instance_id?._id;
        const dict = await buildDictForInstance(instanceId);
        return { drr: dict };
      }

      return null;
    },
    [annotationsMap, instances, validationMode, renderClient]
  );

  const transformForUpdateHandler = useCallback(correctedAnnotations => {
    const updates = [];
    const additions = [];

    Object.entries(correctedAnnotations).forEach(
      ([orientation, annotationData]) => {
        if (annotationData.updates) {
          Object.entries(annotationData.updates).forEach(([id, { values }]) => {
            updates.push({
              _id: id,
              representation: [
                {
                  type: "point",
                  value: values,
                },
              ],
            });
          });
        }

        if (annotationData.additions) {
          Object.entries(annotationData.additions).forEach(
            ([uniqueId, { values }]) => {
              additions.push({
                ID: additions.length,
                name: `drr_validation_${uniqueId}`,
                unique_identifier: parseInt(uniqueId),
                representation: [
                  {
                    type: "point",
                    value: values,
                  },
                ],
                nature: "landmark",
              });
            }
          );
        }
      }
    );

    return { updates, additions };
  }, []);

  const updateCorrectedAnnotations = useCallback(
    async (correctedAnnotations, deletedAnnotationIds = []) => {
      const { updates, additions } =
        transformForUpdateHandler(correctedAnnotations);

      const getDrrInstanceId = () => {
        if (drrAnnotationContext?.instanceId) {
          return drrAnnotationContext.instanceId;
        }

        if (instances && typeof instances === "object") {
          if (instances.ct_instance_id && instances.ct_instance_id._id) {
            return instances.ct_instance_id._id;
          }

          const firstInstance = Object.values(instances)[0];
          if (firstInstance && firstInstance._id) {
            return firstInstance._id;
          }
        }

        return null;
      };

      try {
        if (updates.length > 0) {
          await Promise.all(
            updates.map(update =>
              updateAnnotationsInDatabase(update, update._id)
            )
          );
        }

        const needsInstanceMutation =
          additions.length > 0 || deletedAnnotationIds.length > 0;

        if (needsInstanceMutation) {
          const instanceId = getDrrInstanceId();

          if (!instanceId) {
            throw new Error("No instance ID found for syncing DRR annotations");
          }

          if (additions.length > 0) {
            await createAnnotationsInDatabase(additions, instanceId);
          }

          if (deletedAnnotationIds.length > 0) {
            await deleteAnnotationsInDatabase(instanceId, deletedAnnotationIds);
          }
        }
      } catch (error) {
        throw new Error("Failed to update annotations: " + error.message);
      }
    },
    [transformForUpdateHandler, instances, drrAnnotationContext]
  );

  const updateInstances = useCallback(
    async isValidated => {
      const instancesToValidate = [];

      if (instances && typeof instances === "object") {
        if (validationMode === SPINESIM_MODALITIES.DX) {
          if (instances.sagittal_dx_instance_id?._id) {
            instancesToValidate.push({
              _id: instances.sagittal_dx_instance_id._id,
            });
          }
          if (instances.coronal_dx_instance_id?._id) {
            instancesToValidate.push({
              _id: instances.coronal_dx_instance_id._id,
            });
          }
        } else if (validationMode === SPINESIM_MODALITIES.DRR) {
          if (instances.ct_instance_id?._id) {
            instancesToValidate.push({
              _id: instances.ct_instance_id._id,
            });
          }
        } else {
          Object.values(instances).forEach(instance => {
            if (instance && instance._id) {
              instancesToValidate.push({
                _id: instance._id,
              });
            }
          });
        }
      }

      if (instancesToValidate.length === 0) {
        return;
      }

      try {
        await Promise.all(
          instancesToValidate.map(instance =>
            validateInstanceInDatabase(instance._id)
          )
        );
      } catch (error) {
        throw new Error("Failed to update instances: " + error.message);
      }
    },
    [instances, validationMode]
  );

  const focusOnRegion = useCallback(
    async region => {
      if (!renderClient || disabled) return;

      try {
        await renderClient.focusOnRegion(PLANNING_STATES.VALIDATE, region);
      } catch (error) {
        setError("Failed to focus on region");
      }
    },
    [renderClient, disabled]
  );

  const handleSpineFilterChange = useCallback(
    filterOption => {
      if (!isDxMode) return;

      setActiveSpineFilter(filterOption);

      const spineStep = activeDxSteps[currentStep];
      if (spineStep && spineStep.isSpineGroup) {
        const selectedSpineOption = spineStep.spineOptions.find(
          option => option.name === filterOption
        );
        if (selectedSpineOption) {
          focusOnRegion(selectedSpineOption.region);
        }
      }
    },
    [currentStep, focusOnRegion, isDxMode, activeDxSteps]
  );

  const updateValidation = useCallback(async () => {
    if (!renderClient || isUpdating || isChecking) return;

    setIsUpdating(true);
    setError(null);

    try {
      // Step 1: Run sanity check before persisting (DX mode only)
      if (validationMode === SPINESIM_MODALITIES.DX) {
        const annoIdDicts = await buildAnnoIdDict();
        if (annoIdDicts) {
          localStorage.setItem("debug_sanity_sagittal", JSON.stringify(annoIdDicts.sagittal));
          localStorage.setItem("debug_sanity_coronal", JSON.stringify(annoIdDicts.coronal));
          const annotationSets = [];

          if (
            annoIdDicts.sagittal &&
            Object.keys(annoIdDicts.sagittal).length > 0
          ) {
            annotationSets.push({
              annotations: annoIdDicts.sagittal,
              imageType: "Sagittal",
            });
          }
          if (
            annoIdDicts.coronal &&
            Object.keys(annoIdDicts.coronal).length > 0
          ) {
            annotationSets.push({
              annotations: annoIdDicts.coronal,
              imageType: "Coronal",
            });
          }

          if (annotationSets.length > 0) {
            const isValid = await checkMultipleAnnotations(annotationSets);
            if (!isValid) {
              setIsUpdating(false);
              return; // Stop here if validation fails
            }
          }
        }
      }

      // Step 2: Continue with existing validation logic
      if (validationMode === SPINESIM_MODALITIES.DX && instances) {
        const functionalEndplateId =
          functionalEndplateOptions.find(
            option => option.label === functionalEndplate
          )?.id || 2;

        const metadata = {
          firstVisibleVertebra: String(firstVisibleVertebra),
          hasT13: hasT13,
          hasS1Lumbarized: l5S1Variation === "S1L",
          hasL5Sacralized: l5S1Variation === "L5S",
          functionalHandplate: functionalEndplateId,
        };


        const instancesToUpdate = [
          instances.sagittal_dx_instance_id?._id,
          instances.coronal_dx_instance_id?._id,
        ].filter(Boolean);


        await Promise.all(
          instancesToUpdate.map(instanceId =>
            updateInstanceMetadata(instanceId, metadata)
          )
        );
      }

      const [annotations, drrPointsState] = await Promise.all([
        renderClient.getUpdatedAnnotations(PLANNING_STATES.VALIDATE),
        validationMode === SPINESIM_MODALITIES.DRR
          ? renderClient.getDrrPointsState(PLANNING_STATES.VALIDATE)
          : Promise.resolve(null),
      ]);

      let deletedAnnotationIds = [];
      if (
        validationMode === SPINESIM_MODALITIES.DRR &&
        drrAnnotationContext?.annotationIdByUniqueId &&
        Array.isArray(drrPointsState)
      ) {
        const currentUniqueIds = new Set(
          drrPointsState
            .map(point => point?.uniqueId)
            .filter(uniqueId => uniqueId !== null && uniqueId !== undefined)
            .map(uniqueId => Number(uniqueId))
            .filter(uniqueId => !Number.isNaN(uniqueId))
        );

        drrAnnotationContext.annotationIdByUniqueId.forEach(
          (annotationId, uniqueId) => {
            if (!currentUniqueIds.has(uniqueId)) {
              deletedAnnotationIds.push(annotationId);
            }
          }
        );
      }

      await Promise.all([
        updateCorrectedAnnotations(annotations, deletedAnnotationIds),
        updateInstances(),
      ]);

      if (refreshData) {
        await refreshData();
      }

      if (onCheckReloadRequested) {
        onCheckReloadRequested();
      }

      await new Promise(resolve => setTimeout(resolve, 500));

      navigateToCheck(routeState.params.patientId, routeState.params.twinId);
    } catch (error) {
      setError("Failed to complete validation: " + error.message);
    } finally {
      setIsUpdating(false);
    }
  }, [
    renderClient,
    isUpdating,
    isChecking,
    validationMode,
    instances,
    functionalEndplateOptions,
    functionalEndplate,
    firstVisibleVertebra,
    hasT13,
    l5S1Variation,
    buildAnnoIdDict,
    checkMultipleAnnotations,
    updateCorrectedAnnotations,
    updateInstances,
    refreshData,
    navigateToCheck,
    routeState.params.patientId,
    routeState.params.twinId,
    onCheckReloadRequested,
    drrAnnotationContext,
  ]);

  const handleAcceptAll = useCallback(() => {
    if (shouldBlockTransitionalNavigation) return;

    showPopup(
      <ValidationConfirmationModal
        onClose={hidePopup}
        onContinue={async () => {
          hidePopup();
          const totalSteps = isDxMode ? activeDxSteps.length : stepsDrr.length;
          const allSteps = Array.from({ length: totalSteps }, (_, i) => i);
          setValidatedSteps(new Set(allSteps));
          await updateValidation();
        }}
        modality={validationMode}
      />
    );
  }, [
    showPopup,
    hidePopup,
    updateValidation,
    validationMode,
    isDxMode,
    activeDxSteps,
    shouldBlockTransitionalNavigation,
  ]);

  const handleVertebraChange = useCallback(e => {
    setFirstVisibleVertebra(e.target.value);
  }, []);

  const handleTransitionalVertebra = useCallback(e => {
    const value = e.target.value === "yes";
    setHasTransitionalVertebra(value);
    if (!value) {
      setL5S1Variation("None");
      setHasT13(false);
    }
  }, []);

  const handleT13 = useCallback(e => {
    const value = e.target.value === "yes";
    setHasT13(value);
  }, []);

  const handleL5S1Variation = useCallback(e => {
    const value = e.target.value;
    setL5S1Variation(value);
  }, []);

  const handleRerunModel = useCallback(async () => {
    if (!onRerunLandmarking || isUpdating) return;

    setIsUpdating(true);
    setError(null);

    try {
      const metadata = {
        firstVisibleVertebra: String(firstVisibleVertebra),
        hasT13: hasT13,
        hasS1Lumbarized: l5S1Variation === "S1L",
        hasL5Sacralized: l5S1Variation === "L5S",
      };

      await onRerunLandmarking(metadata);
      setLastRerunTransitionalState(currentTransitionalState);
      lastRerunInitializationTokenRef.current = viewReloadToken;
    } catch (error) {
      setError(`Failed to rerun model: ${error.message}`);
    } finally {
      setIsUpdating(false);
    }
  }, [
    onRerunLandmarking,
    firstVisibleVertebra,
    hasT13,
    l5S1Variation,
    currentTransitionalState,
    viewReloadToken,
    isUpdating,
  ]);

  useEffect(() => {
    const fetchFunctionalEndplateOptions = async () => {
      if (!renderClient || !isDxMode) return;

      try {
        const options = await renderClient.getFunctionalEndplateOptions(
          PLANNING_STATES.VALIDATE
        );
        setFunctionalEndplateOptions(options);

        if (options && options.length > 0 && !functionalEndplate) {
          setFunctionalEndplate(options[0].label);
        }
      } catch (error) {
        console.error("Failed to fetch functional endplate options:", error);
        setFunctionalEndplateOptions([]);
      }
    };

    fetchFunctionalEndplateOptions();
  }, [renderClient, isDxMode]);

  useEffect(() => {
    if (functionalEndplateTouched || functionalEndplateOptions.length === 0)
      return;

    if (instances) {
      const sagittalInstance = instances.sagittal_dx_instance_id;
      const coronalInstance = instances.coronal_dx_instance_id;
      const sourceInstance = sagittalInstance || coronalInstance;

      if (
        sourceInstance?.functional_handplate !== null &&
        sourceInstance?.functional_handplate !== undefined
      ) {
        return;
      }
    }

    if (l5S1Variation === "L5S") {
      setFunctionalEndplate("L5 upper endplate");
    } else if (l5S1Variation === "S1L") {
      setFunctionalEndplate("S2 upper endplate");
    } else {
      setFunctionalEndplate("S1 upper endplate");
    }
  }, [
    l5S1Variation,
    functionalEndplateTouched,
    functionalEndplateOptions,
    instances,
  ]);

  const handleFunctionalEndplateChange = useCallback(async (e) => {
    const selectedValue = e.target.value;
    setFunctionalEndplate(selectedValue);
    setFunctionalEndplateTouched(true);

    // Focus camera on the selected endplate
    if (renderClient && functionalEndplateOptions.length > 0) {
      const selectedOption = functionalEndplateOptions.find(
        option => option.label === selectedValue
      );
      if (selectedOption) {
        try {
          await renderClient.focusOnEndplate(PLANNING_STATES.VALIDATE, selectedOption.id);
        } catch (error) {
          console.error("Failed to focus on endplate:", error);
        }
      }
    }
  }, [renderClient, functionalEndplateOptions]);

  useEffect(() => {
    if (!onRegionChange || !isDxMode) return;

    const currentStepData = activeDxSteps[currentStep];
    if (!currentStepData) return;

    let region;
    if (currentStepData.isSpineGroup) {
      region = MacroRegions.SPINE;
    } else if (currentStepData.contentType === "functionalEndplate") {
      region = MacroRegions.ENDPLATES;
    } else {
      region = MacroRegions.LOWER_BODY;
    }

    onRegionChange(region);
  }, [currentStep, onRegionChange, isDxMode, activeDxSteps]);

  useEffect(() => {
    if (!instances) return;

    const sagittalInstance = instances.sagittal_dx_instance_id;
    const coronalInstance = instances.coronal_dx_instance_id;

    const sourceInstance = sagittalInstance || coronalInstance;
    const sourceInstanceId = sourceInstance?._id || null;

    if (!sourceInstance) return;

    if (
      sourceInstance.has_t_13 !== null &&
      sourceInstance.has_t_13 !== undefined
    ) {
      setHasT13(sourceInstance.has_t_13);
    }

    const hasS1Lumbarized = sourceInstance.has_s1_lumbarized === true;
    const hasL5Sacralized = sourceInstance.has_l5_sacralized === true;
    const hasTransitional =
      hasS1Lumbarized || hasL5Sacralized || sourceInstance.has_t_13 === true;

    if (hasTransitional) {
      setHasTransitionalVertebra(true);

      if (hasS1Lumbarized) {
        setL5S1Variation("S1L");
      } else if (hasL5Sacralized) {
        setL5S1Variation("L5S");
      } else {
        setL5S1Variation("None");
      }
    } else {
      setHasTransitionalVertebra(false);
    }

    const transitionalStateFromInstance = {
      hasTransitionalVertebra: hasTransitional,
      hasT13: sourceInstance.has_t_13 === true,
      l5S1Variation: hasS1Lumbarized ? "S1L" : hasL5Sacralized ? "L5S" : "None",
    };

    const shouldResetRerunBaseline =
      lastRerunInitializationTokenRef.current !== viewReloadToken ||
      !lastRerunTransitionalState ||
      lastBaselineInstanceIdRef.current !== sourceInstanceId;

    if (shouldResetRerunBaseline) {
      setLastRerunTransitionalState(transitionalStateFromInstance);
      lastRerunInitializationTokenRef.current = viewReloadToken;
      lastBaselineInstanceIdRef.current = sourceInstanceId;
    }

    if (sourceInstance.vertebra) {
      const vertebraValue = sourceInstance.vertebra;

      const matchingOption = vertebraeItems.find(
        item => String(item.value) === String(vertebraValue)
      );
      if (matchingOption) {
        setFirstVisibleVertebra(matchingOption.value);
      }
    }

    if (
      sourceInstance.functional_handplate !== null &&
      sourceInstance.functional_handplate !== undefined &&
      functionalEndplateOptions.length > 0
    ) {
      const endplateId = sourceInstance.functional_handplate;
      const matchingOption = functionalEndplateOptions.find(
        option => option.id === endplateId
      );
      if (matchingOption) {
        setFunctionalEndplate(matchingOption.label);
        setFunctionalEndplateTouched(true);
      } else {
      }
    }
  }, [
    instances,
    vertebraeItems,
    functionalEndplateOptions,
    viewReloadToken,
    lastRerunTransitionalState,
  ]);

  useEffect(() => {
    if (isLoadingViews || !renderClient || disabled) return;

    const timer = setTimeout(async () => {
      const steps = isDxMode ? activeDxSteps : stepsDrr;
      const step = steps[currentStep];
      if (!step) return;

      if (isDxMode && step.isSpineGroup) {
        const activeSpineOption = step.spineOptions.find(
          option => option.name === activeSpineFilter
        );
        if (activeSpineOption) {
          focusOnRegion(activeSpineOption.region);
        }
      } else if (step.contentType === "functionalEndplate") {
        // Focus on the functional endplate
        if (functionalEndplate && functionalEndplateOptions.length > 0) {
          const selectedOption = functionalEndplateOptions.find(
            option => option.label === functionalEndplate
          );
          if (selectedOption) {
            try {
              await renderClient.focusOnEndplate(PLANNING_STATES.VALIDATE, selectedOption.id);
            } catch (error) {
              console.error("Failed to focus on endplate:", error);
            }
          }
        }
      } else if (step.region) {
        focusOnRegion(step.region);
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [
    focusOnRegion,
    isLoadingViews,
    renderClient,
    currentStep,
    activeSpineFilter,
    isDxMode,
    activeDxSteps,
    functionalEndplate,
    functionalEndplateOptions,
    viewReloadToken,
    disabled,
  ]);

  useEffect(() => {
    const steps = isDxMode ? activeDxSteps : stepsDrr;
    if (currentStep >= steps.length) {
      setCurrentStep(0);
    }
  }, [isDxMode, currentStep, activeDxSteps]);

  useEffect(() => {
    setValidatedSteps(new Set());
  }, [isDxMode]);

  const getCurrentSubtasks = () => {
    if (!isDxMode) return [];

    const step = activeDxSteps[currentStep];
    if (!step) return [];

    if (step.isSpineGroup) {
      const activeSpineOption = step.spineOptions.find(
        option => option.name === activeSpineFilter
      );
      return activeSpineOption?.subtasks || [];
    }
    return step.subtasks || [];
  };

  const rerunSection = () => {
    return (
      <div className="subtask-rerun-section-content">
        <div className="subtask-rerun-title validation-timeline-info-text App-text-large">
          Review if model parameters are correct:
        </div>
        <LabeledSelect
          label={"First visible vertebra from top:"}
          items={vertebraeItems}
          onChange={handleVertebraChange}
          selectedItem={firstVisibleVertebra}
        />
        <div className="Vertebra-select-info">
          <RadioButtonFlat
            options={transitionalVertebraOptions}
            selectedOption={hasTransitionalVertebra ? "yes" : "no"}
            onChange={handleTransitionalVertebra}
            name="transitionalVertebra"
            displayName="Are there transitional vertebra?"
          />
          {hasTransitionalVertebra && (
            <div className="Vertebra-options">
              <div className="Vertebra-select-info">
                <RadioButtonFlat
                  options={t13Options}
                  selectedOption={hasT13 ? "yes" : "no"}
                  onChange={handleT13}
                  name="t13"
                  displayName="T13 Present"
                />
              </div>

              <div className="Vertebra-select-info">
                <RadioButtonFlat
                  options={l6Options}
                  selectedOption={l5S1Variation || "None"}
                  onChange={handleL5S1Variation}
                  name="l5S1Variation"
                  displayName="L5/S1 Variation"
                />
              </div>
            </div>
          )}
        </div>
        <div className="rerun-button-container">
          <Button
            value={isUpdating ? "Rerunning..." : "Rerun"}
            onClick={handleRerunModel}
            disabled={isUpdating || disabled}
          />
        </div>
      </div>
    );
  };

  const handleNextStep = useCallback(async () => {
    if (disabled || shouldBlockTransitionalNavigation) return;

    setValidatedSteps(prev => new Set(prev).add(currentStep));

    const steps = isDxMode ? activeDxSteps : stepsDrr;
    if (currentStep < steps.length - 1) {
      const nextIndex = currentStep + 1;
      setCurrentStep(nextIndex);
      const step = steps[nextIndex];
      if (isDxMode && step.isSpineGroup) {
        const activeSpineOption = step.spineOptions.find(
          option => option.name === activeSpineFilter
        );
        if (activeSpineOption) {
          await focusOnRegion(activeSpineOption.region);
        }
      } else if (step.region) {
        await focusOnRegion(step.region);
      }
    }
  }, [
    currentStep,
    activeSpineFilter,
    focusOnRegion,
    isDxMode,
    disabled,
    activeDxSteps,
    shouldBlockTransitionalNavigation,
  ]);

  const handlePrevStep = useCallback(async () => {
    if (disabled) return;

    if (currentStep > 0) {
      const prevIndex = currentStep - 1;
      setCurrentStep(prevIndex);
      const steps = isDxMode ? activeDxSteps : stepsDrr;
      const step = steps[prevIndex];
      if (isDxMode && step?.isSpineGroup) {
        const activeSpineOption = step.spineOptions.find(
          option => option.name === activeSpineFilter
        );
        if (activeSpineOption) {
          await focusOnRegion(activeSpineOption.region);
        }
      } else if (step?.region) {
        await focusOnRegion(step.region);
      }
    }
  }, [
    currentStep,
    activeSpineFilter,
    focusOnRegion,
    isDxMode,
    disabled,
    activeDxSteps,
  ]);

  const renderSubtaskList = (subtasks, { useBullets = true } = {}) => {
    if (!subtasks || subtasks.length === 0) {
      return null;
    }

    return (
      <div>
        {subtasks.map((subtask, subtaskIndex) => (
          <div
            key={`${subtaskIndex}-${subtask}`}
            className="subtask validation-timeline-info-text App-text-medium"
          >
            {useBullets ? `• ${subtask}` : subtask}
          </div>
        ))}
      </div>
    );
  };

  const renderDxStepContent = step => {
    const subtasks = getCurrentSubtasks();

    if (step.isSpineGroup) {
      return (
        <div className="subtask-step-content">
          {step.subtasksLabel && (
            <div className="subtask-header  validation-timeline-info-text App-text-medium">
              {step.subtasksLabel}
            </div>
          )}
          {renderSubtaskList(subtasks)}
          {step.note && (
            <div className="subtask-comment validation-timeline-info-text App-text-medium">
              {step.note}
            </div>
          )}
          <div className="subtask-rerun-section">
            <div className="subtask-rerun-text validation-timeline-info-text App-text-large">
              OR
            </div>
            <WidgetBox
              children={rerunSection()}
              title={"Rerun AI Model"}
              noBgColor
              isExpandable
              isExpanded={isRerunExpanded}
              onToggleExpand={() => setIsRerunExpanded(prev => !prev)}
              isSmall
            />
          </div>
        </div>
      );
    }

    if (step.contentType === "functionalEndplate") {
      return (
        <div className="subtask-step-content">
          {renderSubtaskList(subtasks, { useBullets: false })}
          <div className="subtask-rerun-section-content">
            <Select
              items={functionalEndplateLabels}
              onChange={handleFunctionalEndplateChange}
              selectedItem={functionalEndplate}
            />
          </div>
        </div>
      );
    }

    return (
      <div className="subtask-step-content">
        {step.subtasksLabel && (
          <div className="validation-timeline-info-text App-text-medium">
            {step.subtasksLabel}
          </div>
        )}
        {renderSubtaskList(subtasks)}
        {step.note && (
          <div className="validation-timeline-info-text App-text-medium">
            {step.note}
          </div>
        )}
      </div>
    );
  };

  const validationWidgetDx = () => {
    return (
      <div className="validation-timeline-wrapper">
        <div className="validation-timeline-subtitle validation-timeline-info-text App-text-large">
          Review and edit annotations as needed to ensure accurate spinopelvic
          measurements and surgical simulations.
        </div>
        <div className="validation-timeline">
          <div className="timeline-steps">
            <div className="validation-timeline-line"></div>
            {activeDxSteps.map((step, index) => (
              <div key={index} className="timeline-step">
                <button
                  onClick={e => e.preventDefault()}
                  disabled={true}
                  className={`step-circle ${
                    validatedSteps.has(index) ? "validated" : ""
                  } ${currentStep === index ? "selected" : ""}`}
                />
                <div className="step-details">
                  <div className="step-header">
                    <span className="step-name">{step.name}</span>
                  </div>

                  {/* Show spine filter options when spine step is selected */}
                  {currentStep === index && step.isSpineGroup && (
                    <div className="spine-filter-container">
                      <div className="Display-filter-options">
                        {step.spineOptions.map(option => (
                          <button
                            key={option.name}
                            className={`Display-filter-option App-text-small ${
                              activeSpineFilter === option.name ? "active" : ""
                            }`}
                            onClick={() => handleSpineFilterChange(option.name)}
                            disabled={isUpdating || disabled}
                          >
                            {option.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Content per section */}
                  {currentStep === index && (
                    <div className="subtasks-container">
                      {renderDxStepContent(step)}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="validation-navigation-bar">
          <div className="validation-navigation-buttons">
            {currentStep > 0 && (
              <ButtonWithIcon
                value="Previous"
                onClick={handlePrevStep}
                disabled={isUpdating || disabled}
                icon={IconsEnum.GOBACK}
              />
            )}
            {currentStep < activeDxSteps.length - 1 ? (
              <Button
                value="Next"
                onClick={handleNextStep}
                disabled={
                  isUpdating || disabled || shouldBlockTransitionalNavigation
                }
              />
            ) : (
              <Button
                value={isUpdating ? "Validating" : isChecking ? "Checking..." : "Accept"}
                onClick={handleAcceptAll}
                disabled={
                  isUpdating || isChecking || disabled || shouldBlockTransitionalNavigation
                }
              />
            )}
          </div>
        </div>
      </div>
    );
  };

  const validationWidgetDrr = () => {
    return (
      <div className="validation-timeline-wrapper">
        <div className="validation-timeline-subtitle validation-timeline-info-text App-text-large">
          You need to define at least 3 vertebrae to proceed and visualise
          radiodensity.
        </div>
        <div className="validation-timeline">
          <div className="timeline-steps">
            <div className="validation-timeline-line"></div>
            {stepsDrr.map((step, index) => (
              <div key={index} className="timeline-step">
                <button
                  onClick={e => e.preventDefault()}
                  disabled={true}
                  className={`step-circle ${
                    validatedSteps.has(index) ? "validated" : ""
                  } ${currentStep === index ? "selected" : ""}`}
                />
                <div className="step-details">
                  <div className="step-header">
                    <span className="step-name">{step.name}</span>
                  </div>

                  {/* Content per section */}
                  {currentStep === index && (
                    <div className="subtasks-container">
                      <div className="subtask-step-content">
                        {renderSubtaskList(step.subtasks, {
                          useBullets: false,
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="validation-navigation-bar">
          <div className="validation-navigation-buttons">
            {currentStep > 0 && (
              <ButtonWithIcon
                value="Previous"
                onClick={handlePrevStep}
                disabled={isUpdating || disabled}
                icon={IconsEnum.GOBACK}
              />
            )}
            {currentStep < stepsDrr.length - 1 ? (
              <Button
                value="Next"
                onClick={handleNextStep}
                disabled={isUpdating || disabled}
              />
            ) : (
              <Button
                value={isUpdating ? "Validating" : "Accept"}
                onClick={handleAcceptAll}
                disabled={isUpdating || disabled || !allDrrLabelsAssigned}
              />
            )}
          </div>
        </div>
      </div>
    );
  };

  const validationTitle = useMemo(() => {
    if (validationMode === SPINESIM_MODALITIES.DX) {
      return "Validate DX Annotations";
    } else if (validationMode === SPINESIM_MODALITIES.DRR) {
      return "Validate DRR Annotations";
    }
    return "Validate Automatic Annotations";
  }, [validationMode]);

  const renderValidationWidget = () => {
    if (isDxMode) return validationWidgetDx();
    if (validationMode === SPINESIM_MODALITIES.DRR)
      return validationWidgetDrr();
    return validationWidgetDx();
  };

  return (
    <div className="validation-timeline-container">
      <SimpleWidgetBox
        children={renderValidationWidget()}
        className="validation-timeline-widget"
        title={validationTitle}
      />
    </div>
  );
};

export default ValidationTimeline;
